// Pure, axios-free quantity math for the debundle workflow, extracted from
// DebundleQuantityAndLocation.tsx and DebundleChildSKUAllocator.tsx so the high-risk clamp /
// aggregate / allocation logic (which moves real inventory) is unit-testable. Both components are
// owned by this workflow and are rewired to call these helpers — behaviour is identical.

import { ChildSKUAllocation, SourceShelfAllocation } from '../components/debundle/types';

// ── DebundleQuantityAndLocation: quantity clamp + per-shelf clamp + completeness ────────────────

// Clamp the requested debundle quantity to [1, maxDebundle] (mirrors the inline
// Math.max(1, Math.min(value, maxDebundle))). Used on the quantity input.
export const clampDebundleQuantity = (value: number, maxDebundle: number): number =>
  Math.max(1, Math.min(value, maxDebundle));

// Clamp a per-shelf "take" quantity to [0, available] (mirrors
// Math.max(0, Math.min(newAllocated, availableQuantity))).
export const clampShelfAllocation = (newAllocated: number, available: number): number =>
  Math.max(0, Math.min(newAllocated, available));

// Sum of allocated quantities across source shelves.
export const totalAllocated = (allocations: SourceShelfAllocation[]): number =>
  allocations.reduce((sum, a) => sum + a.allocatedQuantity, 0);

// Units still to allocate to hit the target quantity (may be negative when over-allocated).
export const remainingToAllocate = (
  quantity: number,
  allocations: SourceShelfAllocation[],
): number => quantity - totalAllocated(allocations);

// Allocation is complete when the allocated total exactly matches a positive target quantity.
export const isAllocationComplete = (
  quantity: number,
  allocations: SourceShelfAllocation[],
): boolean => totalAllocated(allocations) === quantity && quantity > 0;

// ── DebundleChildSKUAllocator: allocation reducer + progress + completeness ─────────────────────

// Sum of units already allocated for a single child SKU across its shelf allocations.
export const allocatedForSKU = (sku: ChildSKUAllocation): number =>
  sku.allocations.reduce((sum, a) => sum + a.quantity, 0);

export interface ApplyAllocationResult {
  // true when the requested quantity was accepted; false when it was rejected (exceeds remaining,
  // or an invalid quantity / unknown SKU / empty shelf). On rejection `allocations` is returned
  // unchanged so callers can surface a toast without mutating state.
  ok: boolean;
  allocations: ChildSKUAllocation[];
  // Remaining capacity for the SKU at call time — handy for a "cannot allocate more than N" message.
  remaining: number;
}

// Pure reducer for DebundleChildSKUAllocator.handleAddAllocation: adds `quantity` of `skuId` to
// `shelfLocation`, capping at the SKU's remaining need and merging into an existing shelf entry
// rather than pushing a duplicate. Returns a NEW allocations array on success (source is never
// mutated); on rejection returns the original array with ok=false.
export const applyAllocation = (
  allocations: ChildSKUAllocation[],
  skuId: string,
  shelfLocation: string,
  quantity: number,
): ApplyAllocationResult => {
  const skuIndex = allocations.findIndex((s) => s.skuId === skuId);
  if (skuIndex === -1 || !shelfLocation.trim() || quantity <= 0) {
    return { ok: false, allocations, remaining: 0 };
  }

  const sku = allocations[skuIndex];
  const remaining = sku.totalQuantity - allocatedForSKU(sku);
  if (quantity > remaining) {
    return { ok: false, allocations, remaining };
  }

  // Deep-ish copy of the mutated SKU so the input array/objects are never mutated.
  const nextShelfAllocations = sku.allocations.map((a) => ({ ...a }));
  const existingIndex = nextShelfAllocations.findIndex((a) => a.shelfLocation === shelfLocation);
  if (existingIndex > -1) {
    nextShelfAllocations[existingIndex] = {
      ...nextShelfAllocations[existingIndex],
      quantity: nextShelfAllocations[existingIndex].quantity + quantity,
    };
  } else {
    nextShelfAllocations.push({ shelfLocation, quantity });
  }

  const next = allocations.slice();
  next[skuIndex] = { ...sku, allocations: nextShelfAllocations };
  return { ok: true, allocations: next, remaining };
};

// Progress percentage (0-100) for a single child SKU, with the divide-by-zero guard
// (totalQuantity === 0 -> 0). Capped at 100.
export const getProgressForSKU = (sku: ChildSKUAllocation | undefined): number => {
  if (!sku || sku.totalQuantity === 0) return 0;
  return Math.min(100, (allocatedForSKU(sku) / sku.totalQuantity) * 100);
};

// All child SKUs fully allocated (allocated === totalQuantity for every SKU).
export const allAllocationsComplete = (allocations: ChildSKUAllocation[]): boolean =>
  allocations.every((sku) => allocatedForSKU(sku) === sku.totalQuantity);
